import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from "@angular/core";
import { Router } from "@angular/router";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { forkJoin, map, Observable, Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "@progress/kendo-angular-dialog";
import { CancelConfirmationComponent } from "../settings-dashboard-management-component/cancel-confirmation/cancel-confirmation.component";
import type { DashboardSelectCanChangeArgs } from "@shared/dashboard-select";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";

import {
    CardViewService,
    CategoriesService,
    HelpersService,
    ResponseErrorHandlerService,
    AuthService,
    SettingsBreadcrumbService,
    AnalyticsService,
} from "../../shared";
import { DashboardsService } from "../../shared/dashboards-service/dashboards.service";
import { UtilsService } from "../../shared/utils/utils.service";
import { AddEditComponent } from "@shared/components";
import { ApplicationType } from "@app/views-interfaces/ApplicationType.enum";
import { DragAndDropService, HighlightOriginStrategy, provideDragAndDrop } from "@app/drag-and-drop";

@Component({
    selector: "wk-settings-files-category",
    templateUrl: "./settings-files-category.component.html",
    styleUrl: "./settings-files-category.component.scss",
    standalone: false,
    host: {
        class: "wk-sfc",
    },
    providers: [provideDragAndDrop({ outOfBoundsStrategy: new HighlightOriginStrategy() })],
})
export class SettingsFilesCategoryComponent implements OnInit, OnDestroy {
    private helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private cardViewService = inject(CardViewService);
    private responseErrorHandlerService = inject(ResponseErrorHandlerService);
    categoriesService = inject(CategoriesService);
    private dashboardsService = inject(DashboardsService);
    private modalService = inject(BsModalService);
    private readonly translate = inject(TranslateService);
    private readonly dialogService = inject(DialogService);
    private authService = inject(AuthService);
    private cdr = inject(ChangeDetectorRef);
    private breadcrumbService = inject(SettingsBreadcrumbService);
    private readonly router = inject(Router);
    protected readonly dragAndDropService = inject(DragAndDropService);

    isEditingSubscription: Subscription;
    fileCategoryModel: any = {
        tagsDragInProgerss: false,
        categoriesDragInProgerss: false,
    };
    tempId = 0;
    isLastOneToTheRightTagVisible = true;
    thisIsIE = false;
    isEditInProgress = false;
    AssignCategoryNoTags;
    AssignCategoryId;
    bsModalRef?: BsModalRef;
    isSidebarCollapsed = false;
    private subs = new Subscription();
    protected isFormChanged = signal<boolean>(false);
    ProfileName = "";
    @ViewChild("deleteDialogContent") deleteDialogContent!: TemplateRef<any>;
    private subscription: Subscription;
    deletedCategoryItems: { categoryId: string; index: number }[] = [];
    protected isDashboardChanging = signal<boolean>(false);
    autoScrollId;
    currentDashboard: IDashboardModel;
    isJobsBillingDashboard = false;
    showCategories = true;
    protected readonly isDragging = this.dragAndDropService.isDragging;

    constructor() {
        const helpersService = this.helpersService;

        this.subscription = this.dashboardsService.currentDashboardChanged$.subscribe(() => {
            this.init();
        });

        this.isEditingSubscription = helpersService.placeLabelIsEditProgressEvent$.subscribe((isEditing) => {
            this.isEditInProgress = isEditing;
        });
    }

    async ngOnInit() {
        this.breadcrumbService.setBreadcrumbs("categories");
        this.analyticsService.track("Access Return Categories");
        if (this.authService.loggedIn()) {
            this.init();
        }

        if ("function" !== typeof DataTransfer.prototype.setDragImage) {
            this.thisIsIE = true;
        }
    }

    ngOnDestroy() {
        this.isEditingSubscription.unsubscribe();
        this.subs.unsubscribe();

        this.subscription.unsubscribe();
    }

    async init() {
        this.showCategories = false;
        this.cdr.detectChanges();

        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isJobsBillingDashboard = this.currentDashboard.ApplicationType === ApplicationType.JobsBilling;
        this.fileCategoryModel.Categories = UtilsService.recursiveDeepCopy(await this.categoriesService.getCategoriesList()).sort(function (a, b) {
            return a.Order - b.Order;
        });
        this.fileCategoryModel.tagsModel = UtilsService.recursiveDeepCopy(this.helpersService.getT1Tags());

        for (const category of this.fileCategoryModel.Categories) {
            category.Tags = category.Tags.filter((tag) => tag !== null && tag !== undefined);
        }

        this.fileCategoryModel.Categories = this.categoriesService.fillTagsByIdForCategories(
            this.fileCategoryModel.Categories,
            this.helpersService.getT1Tags(),
        );

        this.fileCategoryModel.showScroll = false;
        this.fileCategoryModel.categoriesDragInProgerss = false;
        this.fileCategoryModel.tagsDragInProgerss = false;
        this.isLastOneToTheRightTagVisible = this.currentDashboard.TaxYear < 2025;
        this.AssignCategoryNoTags = UtilsService.recursiveDeepCopy(this.currentDashboard.AssignCategoryNoTags);
        this.AssignCategoryId = UtilsService.recursiveDeepCopy(this.currentDashboard.AssignCategoryId);
        this.removeAssignedTags();

        setTimeout(() => {
            this.showCategories = true;
            this.cdr.detectChanges();
        }, 0);

        this.helpersService.suppressSpinner();
    }

    removeAssignedTags(): any {
        this.fileCategoryModel.Categories.forEach((category) => {
            category.Tags.forEach((tag) => {
                for (let i = 0; i < this.fileCategoryModel.tagsModel.length; i++) {
                    if (this.fileCategoryModel.tagsModel[i].Id === tag.Id) {
                        this.fileCategoryModel.tagsModel.splice(i, 1);
                    }
                }
            });
        });
    }

    onCancelChanges() {
        if (this.isFormChanged()) {
            this.openPendingChangesDialogConfirmation().subscribe(async (result) => {
                if (result == 1) {
                    this.router.navigate(["/settings/main"]);
                } else if (result == 2) {
                    return;
                }
            });
        } else {
            this.router.navigate(["/settings/main"]);
        }
    }

    onBreadcrumbNavigate(event: { route: string; proceed: () => void }): void {
        if (this.isFormChanged()) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result === 1) {
                    this.isFormChanged.set(false);
                    event.proceed();
                }
            });
        } else {
            event.proceed();
        }
    }

    openPendingChangesDialogConfirmation(): Observable<number> {
        const dialog = this.dialogService.open({
            title: this.translate.instant("AccessPendingChanges.Title"),
            content: CancelConfirmationComponent,
            actions: [
                { text: this.translate.instant("General.Discard"), primary: true, choice: 1 },
                { text: this.translate.instant("General.Cancel"), choice: 2 },
            ],
            width: 600,
            cssClass: "access-pending-changes-dialog",
        });
        return dialog.result.pipe(
            map((result: any) => {
                return result.choice;
            }),
        );
    }

    editCategory(selected): void {
        this.bsModalRef = this.modalService.show(AddEditComponent, {
            initialState: {
                title: "Settings.EditCategory",
                data: UtilsService.recursiveDeepCopy(selected),
                callback: (aCategory) => this.onEditSave(selected, aCategory),
            },
            class: "custom-modal-rename-dashboard",
        });
    }

    onEditSave(selected, aCategory) {
        selected.Name["en-CA"] = aCategory.Name["en-CA"];
        selected.Name["fr-CA"] = aCategory.Name["fr-CA"];
        this.bsModalRef.hide();
        this.isFormChanged.set(true);
    }

    onAddNewCategory(category) {
        this.fileCategoryModel.Categories.push({
            Id: category.Id,
            tempId: ++this.tempId,
            Name: {
                "en-CA": category.Name["en-CA"],
                "fr-CA": category.Name["fr-CA"],
            },
            Tags: [],
        });
        this.isFormChanged.set(true);
    }

    addCategory() {
        const newCategory = {
            Id: crypto.randomUUID(),
            tempId: "",
            Name: {
                "en-CA": "",
                "fr-CA": "",
            },
            Tags: [],
        };
        this.bsModalRef = this.modalService.show(AddEditComponent, {
            initialState: {
                title: "Settings.AddCategory",
                data: UtilsService.recursiveDeepCopy(newCategory),

                callback: (aCategory) => this.onAddNewCategory(aCategory),
            },
            class: "custom-modal-rename-dashboard",
        });
    }

    getCategoryName(category) {
        return category?.Name[this.helpersService.getLang()];
    }

    onDrop() {
        this.isFormChanged.set(true);
    }

    public onConfirm(result) {
        this.bsModalRef.hide();
        this.bsModalRef.content = result;
        this.isFormChanged.set(true);
    }

    showDeletePopup(categoryId: string, i: number) {
        const category = this.fileCategoryModel.Categories[i];
        this.ProfileName = this.getCategoryName(category);
        this.bsModalRef = this.modalService.show(this.deleteDialogContent, {
            class: "custom-modal-rename-dashboard",
        });

        this.bsModalRef.onHidden.subscribe(() => {
            if (this.bsModalRef.content == true) {
                if (!category.tempId) {
                    this.deletedCategoryItems.push({ categoryId, index: i });
                }
                this.fileCategoryModel.tagsModel = this.fileCategoryModel.tagsModel.concat(this.fileCategoryModel.Categories[i].Tags);
                this.fileCategoryModel.Categories.splice(i, 1);
                this.categoriesService.setCategories(JSON.parse(JSON.stringify(this.fileCategoryModel.Categories)));
            }
        });
    }

    deleteCategory(categoryId, i): Promise<void> {
        return new Promise((resolve, reject) => {
            this.categoriesService.isSaving = ++this.categoriesService.isSaving;
            if (categoryId) {
                this.categoriesService.deleteCategory(categoryId).subscribe(
                    () => {
                        this.categoriesService.refresh();
                        this.categoriesService.isSaving = --this.categoriesService.isSaving;
                        resolve();
                    },
                    (error) => {
                        this.categoriesService.isSaving = --this.categoriesService.isSaving;
                        reject(error);
                    },
                );
            } else {
                this.categoriesService.isSaving = --this.categoriesService.isSaving;
                resolve();
            }
        });
    }

    async onSave() {
        if (this.categoriesService.isSaving === 0) {
            if (this.deletedCategoryItems.length > 0) {
                for (const item of this.deletedCategoryItems) {
                    await this.deleteCategory(item.categoryId, item.index);
                }
                this.deletedCategoryItems = [];
            }
            this.analyticsService.track("Save Return Categories");
            this.categoriesService.setCategories(UtilsService.recursiveDeepCopy(this.fileCategoryModel.Categories));
            this.cardViewService.settingsSectionChanged = true;

            const requests = [];
            requests.push(this.categoriesService.saveCategories(this.fileCategoryModel.Categories));

            let assignCategory = undefined;
            if (
                (this.isLastOneToTheRightTagVisible && this.AssignCategoryNoTags !== this.currentDashboard.AssignCategoryNoTags) ||
                !this.AssignCategoryId
            ) {
                requests.push(await this.saveCategoryNoTags(this.currentDashboard));
            } else {
                assignCategory = this.fileCategoryModel.Categories.find((it) => it.Id == this.AssignCategoryId);
            }
            forkJoin(requests).subscribe(
                (responses) => {
                    this.categoriesService.refresh();
                    if (!this.isLastOneToTheRightTagVisible && this.AssignCategoryId) {
                        this.categoriesService.getCategoriesList().then(
                            (response) => {
                                if (assignCategory) {
                                    const jsonAssignCategory = JSON.stringify(assignCategory.Tags.map((it) => it.Id));
                                    const jsonAssignCategoryName = JSON.stringify(assignCategory.Name);
                                    const savedAssignCategory = response.find(
                                        (it) => JSON.stringify(it.Tags) === jsonAssignCategory && JSON.stringify(it.Name) === jsonAssignCategoryName,
                                    );
                                    this.AssignCategoryId = savedAssignCategory.Id;
                                }
                                this.saveCategoryNoTags(this.currentDashboard).subscribe(
                                    () => {
                                        this.isFormChanged.set(false);
                                        this.responseErrorHandlerService.showPopup({
                                            typeLayout: "SUCCESS",
                                            messageCode: "successfullySaved",
                                        });
                                    },
                                    () => {
                                        this.responseErrorHandlerService.showPopup({
                                            typeLayout: "ERROR",
                                            messageCode: "CancelSaved",
                                        });
                                    },
                                );
                            },
                            () => {
                                this.responseErrorHandlerService.showPopup({
                                    typeLayout: "ERROR",
                                    messageCode: "CancelSaved",
                                });
                            },
                        );
                    } else {
                        this.isFormChanged.set(false);
                        this.responseErrorHandlerService.showPopup({
                            typeLayout: "SUCCESS",
                            messageCode: "successfullySaved",
                        });
                    }
                },
                () => {
                    this.responseErrorHandlerService.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "CancelSaved",
                    });
                },
            );
        }
    }

    catchAllTagsToggle(on) {
        if (on == "false") {
            this.AssignCategoryNoTags = false;
            this.AssignCategoryId = "";
        } else {
            this.AssignCategoryNoTags = true;
            this.AssignCategoryId = on == "true" ? "" : on;
        }
        this.isFormChanged.set(true);
    }

    saveCategoryNoTags(currentDashboard) {
        return new Observable<void>((observer) => {
            this.categoriesService.isSaving = ++this.categoriesService.isSaving;
            this.dashboardsService.saveAssignCategoryNoTags(currentDashboard, this.AssignCategoryNoTags, this.AssignCategoryId).subscribe(
                () => {
                    this.dashboardsService.refresh(true);
                    this.categoriesService.isSaving = --this.categoriesService.isSaving;
                    observer.next();
                    observer.complete();
                },
                () => {
                    this.categoriesService.isSaving = --this.categoriesService.isSaving;
                    observer.error();
                    observer.complete();
                },
            );
        });
    }

    canChangeDashboard(args: DashboardSelectCanChangeArgs) {
        if (this.isFormChanged()) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result <= 2) {
                    if (result === 1) {
                        this.isDashboardChanging.set(true);
                        this.isFormChanged.set(false);
                        if (args) {
                            this.doChangeDashboard(args);
                        }
                    } else {
                        if (args) {
                            args.cancelAction();
                        }
                    }
                } else if (args) {
                    args.cancelAction();
                }
            });
        } else {
            this.isDashboardChanging.set(true);
            this.doChangeDashboard(args);
        }
    }

    doChangeDashboard(args) {
        try {
            args.action(args.dashboardId);
        } catch {
            this.isDashboardChanging.set(false);
        }
    }

    dashboardChangeCompleted(args: boolean) {
        if (args == true) {
            this.isDashboardChanging.set(false);
        }
    }

    showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if (
            (element.nodeName === "DIV" || element.className === "wk-files-category__list-category-name") &&
            element.offsetWidth < element.scrollWidth
        ) {
            element.setAttribute("data-title", element.innerText);
        }
    }
}
